import {
  CalendarOutlined,
  PaperClipOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Button, Input, Modal, Spin, Tooltip, Typography } from 'antd';
import cn from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useLeadCardContext } from '@app/components/LeadCard/context';
import { ActionButtons } from '@app/components/LeadCard/ui/ActionButtons';
import { IntegrationSelector } from '@app/components/LeadCard/ui/IntegrationSelector';
import { AiRecordSlotModal } from '@app/components/LeadCard/ui/MessageComposer/components/AiRecordSlotModal';
import { ComposerModeSelector } from '@app/components/LeadCard/ui/MessageComposer/components/ComposerModeSelector';
import { LeadEmojiPicker } from '@app/components/LeadCard/ui/MessageComposer/components/LeadEmojiPicker';
import { LeadMessageAttachments } from '@app/components/LeadCard/ui/MessageComposer/components/LeadMessageAttachments';
import { LeadMessageTemplatesDropdown } from '@app/components/LeadCard/ui/MessageComposer/components/LeadMessageTemplatesDropdown';
import {
  TaskComposer,
  type TaskComposerRef,
} from '@app/components/LeadCard/ui/MessageComposer/components/TaskComposer';
import {
  COMPOSER_MODES,
  DEFAULT_COMPOSER_MODE,
  type ComposerMode,
} from '@app/components/LeadCard/ui/MessageComposer/data/composerModes';
import { useLeadMessageComposer } from '@app/components/LeadCard/ui/MessageComposer/hooks';
import {
  COMPOSER_MIN_ROWS,
  getComposerMaxRows,
} from '@app/components/LeadCard/ui/MessageComposer/utils';
import { useWindowSize } from '@app/hooks';

import type { ChangeEvent, FC } from 'react';

import './MessageComposer.scss';

const { Text } = Typography;
const { TextArea } = Input;

export const MessageComposer: FC = () => {
  const [composerMode, setComposerMode] = useState<ComposerMode>(
    DEFAULT_COMPOSER_MODE
  );
  const [composerText, setComposerText] = useState('');
  const [isTaskSubmitting, setIsTaskSubmitting] = useState(false);

  const {
    leadCardData,
    refetchLeadCard,
    refreshLatestHistory,
    saveComment,
    isCommentSaving,
    handleSaveOrganizationComment,
    isOrganizationCommentLoading,
    footerActions,
    openBranchSelectionModal,
  } = useLeadCardContext();
  const clientId = leadCardData?.ClientId ?? null;
  const taskComposerRef = useRef<TaskComposerRef>(null);

  const isCommentMode = composerMode === 'comment';
  const isCommentSubmitting = isCommentMode
    ? isCommentSaving
    : isOrganizationCommentLoading;

  const currentModeConfig =
    COMPOSER_MODES.find(({ id }) => id === composerMode) ?? COMPOSER_MODES[0];

  const isMessageMode = composerMode === 'message';

  const handleModeChange = useCallback((mode: ComposerMode) => {
    setComposerText('');
    setComposerMode(mode);
  }, []);

  const handleComposerTextChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
      setComposerText(value);
    },
    []
  );

  const handleCommentSubmit = useCallback(async () => {
    if (isCommentSubmitting) return;

    const trimmedText = composerText.trim();
    if (!trimmedText) return;

    const isSaved = isCommentMode
      ? await saveComment(trimmedText)
      : await handleSaveOrganizationComment(trimmedText);

    if (isSaved) setComposerText('');
  }, [
    composerText,
    handleSaveOrganizationComment,
    isCommentMode,
    isCommentSubmitting,
    saveComment,
  ]);

  const handleTaskSuccess = useCallback(() => {
    setComposerText('');
    refetchLeadCard();
    void refreshLatestHistory();
  }, [refetchLeadCard, refreshLatestHistory]);

  const handleTaskSubmit = useCallback(async () => {
    if (!taskComposerRef.current || isTaskSubmitting) return;
    setIsTaskSubmitting(true);
    try {
      await taskComposerRef.current.submit();
    } finally {
      setIsTaskSubmitting(false);
    }
  }, [isTaskSubmitting]);

  const {
    acceptedAttachmentExtensions,
    attachFilesHandler,
    attachments,
    autoReplyHandler,
    closeRecordSlotModal,
    createRecordFromSlotHandler,
    fileInputRef,
    hasNoIntegrations,
    isAiAutoReplyAvailable,
    isAiCreateRecordAvailable,
    isAutoReplyDisabled,
    isAutoReplyLoading,
    isCreatingRecordFromSlot,
    isRecordSlotDisabled,
    isRecordSlotLoading,
    isRecordSlotModalOpen,
    recordSlotData,
    recordSlotError,
    recordSlotHandler,
    isBranchConfirmationOpen,
    isBranchesLoading,
    isInputDisabled,
    isRecommendChat,
    isTextOnly,
    messageLength,
    sendBlockedReason,
    isSendDisabled,
    isSending,
    isTemplateFilesLoading,
    isTemplatesLoading,
    isTemplatesOpen,
    isUploadingFiles,
    messageChangeHandler,
    messageKeyDownHandler,
    messageMaxLength,
    messageText,
    removeAttachmentHandler,
    selectedFullIntegration,
    selectedIntegration,
    selectedLeadIntegrations,
    sendMessageHandler,
    setIsBranchConfirmationOpen,
    setIsTemplatesOpen,
    integrationSelectHandler,
    emojiSelectHandler,
    templateSelectHandler,
    templates,
    textAreaRef,
    uploadAttachmentsHandler,
    prepareSendMessageHandler,
  } = useLeadMessageComposer();

  // ИИ не подобрал слот — закрываем модалку и отдаём оператора в штатный
  // сценарий ручной записи (выбор филиала → журнал записи).
  const handleCreateRecordManually = useCallback(() => {
    closeRecordSlotModal();
    openBranchSelectionModal();
  }, [closeRecordSlotModal, openBranchSelectionModal]);

  const isStubSendDisabled = composerText.trim() === '';

  const { innerHeight } = useWindowSize();
  const composerAutoSize = useMemo(
    () => ({
      minRows: COMPOSER_MIN_ROWS,
      maxRows: getComposerMaxRows(innerHeight),
    }),
    [innerHeight]
  );

  return (
    <section className="MessageComposer">
      <div className="MessageComposer__toolbar">
        <div className="MessageComposer__toolbar-selectors">
          <ComposerModeSelector
            selectedMode={composerMode}
            onModeChange={handleModeChange}
          />

          {/* With no available integrations the selector renders nothing:
              the overlay inside the input box already explains the state. */}
          {isMessageMode && !hasNoIntegrations && (
            <IntegrationSelector
              integrations={selectedLeadIntegrations}
              selectedIntegration={selectedIntegration}
              isLoading={isBranchesLoading}
              onIntegrationSelect={integrationSelectHandler}
            />
          )}
        </div>

        <ActionButtons actions={footerActions} />
      </div>

      <Spin spinning={isTemplateFilesLoading}>
        <div
          className={[
            'MessageComposer__input-wrapper',
            isMessageMode &&
              isInputDisabled &&
              // The no-integration overlay keeps the box white; the grey
              // disabled tint is only for transient disabled states.
              !hasNoIntegrations &&
              'MessageComposer__input-wrapper--disabled',
            isMessageMode &&
              hasNoIntegrations &&
              'MessageComposer__input-wrapper--no-integrations',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="MessageComposer__input-area">
            {composerMode === 'task' ? (
              <TaskComposer
                ref={taskComposerRef}
                value={composerText}
                onChange={setComposerText}
                clientId={clientId}
                onSuccess={handleTaskSuccess}
              />
            ) : isMessageMode ? (
              <>
                <TextArea
                  className="MessageComposer__input"
                  autoSize={composerAutoSize}
                  disabled={isInputDisabled}
                  maxLength={isRecommendChat ? undefined : messageMaxLength}
                  placeholder={currentModeConfig.placeholder}
                  ref={textAreaRef}
                  value={messageText}
                  onBlur={() => setIsTemplatesOpen(false)}
                  onChange={messageChangeHandler}
                  onFocus={() => setIsTemplatesOpen(true)}
                  onKeyDown={messageKeyDownHandler}
                />

                <LeadMessageTemplatesDropdown
                  isLoading={isTemplatesLoading}
                  isOpen={isTemplatesOpen}
                  templates={templates}
                  onTemplateSelect={templateSelectHandler}
                />
              </>
            ) : (
              <TextArea
                className="MessageComposer__input"
                autoSize={composerAutoSize}
                disabled={false}
                placeholder={currentModeConfig.placeholder}
                value={composerText}
                onChange={handleComposerTextChange}
              />
            )}
          </div>

          <div className="MessageComposer__input-footer">
            {isMessageMode ? (
              <>
                <span
                  className={cn('MessageComposer__char-count', {
                    'MessageComposer__char-count--limit':
                      messageLength >= messageMaxLength,
                  })}
                >
                  {messageLength} / {messageMaxLength}
                </span>
                <div className="MessageComposer__actions MessageComposer__actions--end">
                  {isAiAutoReplyAvailable && (
                    <Tooltip title="Подготовить ответ с помощью ИИ">
                      <Button
                        aria-label="Подготовить ответ с помощью ИИ"
                        className="MessageComposer__ai-button"
                        disabled={isAutoReplyDisabled}
                        icon={
                          <span className="MessageComposer__ai-icon">
                            <span className="MessageComposer__ai-sparkle MessageComposer__ai-sparkle--top" />
                            Ai
                            <span className="MessageComposer__ai-sparkle MessageComposer__ai-sparkle--bottom" />
                          </span>
                        }
                        loading={isAutoReplyLoading}
                        size="small"
                        onClick={autoReplyHandler}
                      />
                    </Tooltip>
                  )}

                  {isAiCreateRecordAvailable && (
                    <Tooltip title="Записать клиента с помощью ИИ">
                      <Button
                        aria-label="Записать клиента с помощью ИИ"
                        className="MessageComposer__ai-button"
                        disabled={isRecordSlotDisabled}
                        icon={<CalendarOutlined />}
                        loading={isRecordSlotLoading}
                        size="small"
                        onClick={recordSlotHandler}
                      />
                    </Tooltip>
                  )}

                  <LeadEmojiPicker
                    disabled={isInputDisabled}
                    onEmojiSelect={emojiSelectHandler}
                  />

                  <Tooltip
                    title={
                      isTextOnly
                        ? 'В Рекомендуй доступны только текстовые сообщения'
                        : 'Прикрепить файл'
                    }
                  >
                    <Button
                      aria-label="Прикрепить файл"
                      disabled={
                        isInputDisabled || isUploadingFiles || isTextOnly
                      }
                      icon={<PaperClipOutlined />}
                      loading={isUploadingFiles}
                      size="small"
                      onClick={attachFilesHandler}
                    />
                  </Tooltip>

                  <input
                    ref={fileInputRef}
                    accept={acceptedAttachmentExtensions.join(',')}
                    className="MessageComposer__file-input"
                    multiple
                    type="file"
                    onChange={uploadAttachmentsHandler}
                  />

                  <Tooltip title="Отправить сообщение">
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={isSending}
                      size="small"
                      disabled={isSendDisabled}
                      onClick={prepareSendMessageHandler}
                    />
                  </Tooltip>
                </div>
              </>
            ) : composerMode === 'task' ? (
              <div className="MessageComposer__actions MessageComposer__actions--end">
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  size="small"
                  loading={isTaskSubmitting}
                  disabled={isTaskSubmitting}
                  onClick={handleTaskSubmit}
                >
                  Создать задачу
                </Button>
              </div>
            ) : (
              <div className="MessageComposer__actions MessageComposer__actions--end">
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  size="small"
                  loading={isCommentSubmitting}
                  disabled={isStubSendDisabled || isCommentSubmitting}
                  onClick={handleCommentSubmit}
                >
                  Добавить комментарий
                </Button>
              </div>
            )}
          </div>

          {isMessageMode && hasNoIntegrations && (
            <div className="MessageComposer__no-integration-overlay">
              <span className="MessageComposer__no-integration-hint">
                У вас нет доступных интеграций для отправки сообщений
              </span>
              <div className="MessageComposer__no-integration-actions">
                <Link to="/integrations">
                  <Button size="small" type="primary">
                    Перейти к интеграциям
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </Spin>

      {isMessageMode && sendBlockedReason && (
        <div className="MessageComposer__channel-hint" role="status">
          {sendBlockedReason}
        </div>
      )}

      {isMessageMode && (
        <LeadMessageAttachments
          attachments={attachments}
          disabled={isSending}
          onRemoveAttachment={removeAttachmentHandler}
        />
      )}

      <Modal
        title="Отправка сообщения"
        open={isBranchConfirmationOpen}
        okText="Отправить"
        cancelText="Отменить"
        okButtonProps={{
          icon: <SendOutlined />,
          loading: isSending,
        }}
        cancelButtonProps={{ disabled: isSending }}
        onCancel={() => setIsBranchConfirmationOpen(false)}
        onOk={sendMessageHandler}
      >
        Вы отправляете сообщение клиенту через <Text strong>интеграцию</Text>{' '}
        филиала
        <Text strong>
          {selectedFullIntegration?.Branch
            ? ` «${selectedFullIntegration.Branch}»`
            : ' без названия'}
        </Text>
        , в котором нет этого клиента. Если вы продолжите, то в филиале будет
        <Text strong> создан клиент</Text>, и ему будет отправлено сообщение.
      </Modal>

      <AiRecordSlotModal
        open={isRecordSlotModalOpen}
        data={recordSlotData}
        isLoading={isRecordSlotLoading}
        isCreating={isCreatingRecordFromSlot}
        error={recordSlotError}
        onClose={closeRecordSlotModal}
        onCreate={createRecordFromSlotHandler}
        onCreateManually={handleCreateRecordManually}
      />
    </section>
  );
};
